import React, { useCallback, useEffect, useState } from "react";
import { Button, InputNumber, Modal, Tag, Typography } from "antd";
import { LockOutlined, UnlockOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import Amount from "../shared/Amount";
import PageToolbar from "../../../../design-system/PageToolbar";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import { Success, Error as ErrorMsg } from "../../../../commons/Messages/Messages";
import { closePeriod, errorText, listPeriods, reopenPeriod } from "./api";
import { PERIOD_STATUS_COLORS } from "./labels";

const STATUS_LABELS = { open: "Open", closed: "Closed", filed: "Filed" };

const PeriodsTab = () => {
  const { t } = useTranslation("accounting");
  const [year, setYear] = useState(new Date().getFullYear());
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listPeriods(year);
      setPeriods(res.data.periods || []);
    } catch (err) {
      ErrorMsg(errorText(err, t("Could not load periods")));
    } finally {
      setLoading(false);
    }
  }, [year, t]);

  useEffect(() => {
    load();
  }, [load]);

  const monthName = m =>
    new Intl.DateTimeFormat(undefined, { month: "long" }).format(new Date(year, m - 1, 1));

  const run = async (period, action) => {
    setBusy(period);
    try {
      if (action === "close") await closePeriod(period);
      else await reopenPeriod(period);
      Success(
        action === "close"
          ? t("Period {{period}} closed", { period })
          : t("Period {{period}} reopened", { period })
      );
      load();
    } catch (err) {
      ErrorMsg(errorText(err, t("Could not change the period")));
    } finally {
      setBusy(null);
    }
  };

  const confirmClose = p =>
    Modal.confirm({
      title: t("Close {{month}} {{year}}?", { month: monthName(p.month), year }),
      okText: t("Close period"),
      okButtonProps: { danger: true },
      content: (
        <div>
          <Typography.Paragraph>
            {t(
              "Closing locks every entry of this month (Festschreibung under GoBD). Locked entries can no longer be edited, only reversed with a Storno. Earlier months must be closed first."
            )}
          </Typography.Paragraph>
          {p.unlocked ? (
            <Typography.Text>
              {t("{{count}} entries will be locked.", { count: p.unlocked })}
            </Typography.Text>
          ) : null}
        </div>
      ),
      onOk: () => run(p.period, "close")
    });

  const confirmReopen = p =>
    Modal.confirm({
      title: t("Reopen {{month}} {{year}}?", { month: monthName(p.month), year }),
      okText: t("Reopen"),
      content: t(
        "The month accepts new entries again. Entries that were already locked stay locked; later months must be open."
      ),
      onOk: () => run(p.period, "reopen")
    });

  const columns = [
    {
      title: t("Month"),
      dataIndex: "month",
      width: 140,
      mobile: "title",
      render: m => monthName(m)
    },
    { title: t("Period"), dataIndex: "period", width: 90, mobile: "hidden" },
    {
      title: t("Status"),
      dataIndex: "status",
      width: 100,
      mobile: "subtitle",
      render: s => <Tag color={PERIOD_STATUS_COLORS[s]}>{t(STATUS_LABELS[s] || s)}</Tag>
    },
    {
      title: t("Entries"),
      dataIndex: "entries",
      width: 80,
      align: "end",
      render: v => <span className="acct-num">{v}</span>
    },
    {
      title: t("Unlocked"),
      dataIndex: "unlocked",
      width: 80,
      align: "end",
      render: v => <span className="acct-num">{v}</span>
    },
    {
      title: t("Revenue (net)"),
      key: "rev",
      width: 130,
      align: "end",
      render: (_, p) => (p.summary ? <Amount cents={p.summary.revenueNet} /> : "")
    },
    {
      title: t("Expenses (net)"),
      key: "exp",
      width: 130,
      align: "end",
      render: (_, p) => (p.summary ? <Amount cents={p.summary.expensesNet} /> : "")
    },
    {
      title: t("VAT out"),
      key: "vo",
      width: 120,
      align: "end",
      render: (_, p) => (p.summary ? <Amount cents={p.summary.vatOut} /> : "")
    },
    {
      title: t("VAT in"),
      key: "vi",
      width: 120,
      align: "end",
      render: (_, p) => (p.summary ? <Amount cents={p.summary.vatIn} /> : "")
    },
    {
      title: t("Closed / filed"),
      key: "dates",
      width: 160,
      render: (_, p) => (
        <span>
          {p.closedAt ? dayjs(p.closedAt).format("DD.MM.YYYY") : ""}
          {p.filedAt ? ` / ${dayjs(p.filedAt).format("DD.MM.YYYY")}` : ""}
        </span>
      )
    },
    {
      key: "actions",
      width: 140,
      render: (_, p) =>
        p.status === "open" ? (
          <Button
            size="small"
            icon={<LockOutlined />}
            loading={busy === p.period}
            onClick={() => confirmClose(p)}
          >
            {t("Close")}
          </Button>
        ) : p.status === "closed" ? (
          <Button
            size="small"
            icon={<UnlockOutlined />}
            loading={busy === p.period}
            onClick={() => confirmReopen(p)}
          >
            {t("Reopen")}
          </Button>
        ) : null
    }
  ];

  return (
    <div>
      <PageToolbar
        filters={
          <InputNumber
            addonBefore={t("Year")}
            value={year}
            onChange={v => v && setYear(v)}
            min={2000}
            max={2100}
          />
        }
      />
      <Typography.Paragraph type="secondary">
        {t("Months close in order; a filed month (UStVA transmitted) cannot be reopened.")}
      </Typography.Paragraph>
      <ResponsiveTable
        rowKey="period"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={periods}
        pagination={false}
        scroll={{ x: 1200 }}
      />
    </div>
  );
};

export default PeriodsTab;
